"use client";

import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ErrorMessage } from "@hookform/error-message";
import { Label } from "radix-ui";
import { type ComponentProps, type ReactNode, forwardRef, useId } from "react";
import { type FieldValues, type RegisterOptions, useFormContext } from "react-hook-form";
import { FormErrorMessage, Typography, cn } from "../../..";

interface TextAreaProps extends ComponentProps<"textarea"> {
	label?: string;
	description?: ReactNode;
	registerOptions?: RegisterOptions<FieldValues>;
	innerClassName?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
	function TextAreaRef(props, ref) {
		const { label, description, registerOptions, className, innerClassName, ...remainingProps } =
			props;
		const methods = useFormContext();

		const registeredProps =
			methods && props.name
				? methods.register(props.name, {
						shouldUnregister: true,
						...registerOptions,
						onChange() {
							return methods.trigger(props.name);
						},
					})
				: { ref };

		const id = useId();
		const errorId = `${id}-error`;
		const descriptionId = `${id}-description`;

		const methodValue: unknown = props.name ? methods?.watch(props.name) : undefined;
		const hasValue = props.value
			? !!props.value
			: Array.isArray(methodValue)
				? !!methodValue.length
				: !!methodValue;
		const hasError = props.name ? methods?.getFieldState(props.name)?.invalid : false;
		const isSuccess = hasValue && !hasError;

		return (
			<div className={cn(className, "relative flex flex-col gap-1")}>
				{!!label && (
					<Label.Root htmlFor={id} className="self-start font-bold">
						{label}
						{props.required && "*"}
					</Label.Root>
				)}
				<textarea
					id={id}
					{...registeredProps}
					{...remainingProps}
					className={cn(
						{
							"border-danger ring-danger focus-visible:border-danger focus-visible:ring-danger":
								hasError,
							"border-shades read-only:bg-shades-light/70 hover:border-primary-medium focus-visible:border-primary-medium focus-visible:ring-primary-medium":
								!hasError,
							"border-b-success": isSuccess,
						},
						"form-input rounded focus:outline-none focus-visible:ring-inset disabled:cursor-not-allowed disabled:bg-shades-light/70 motion-safe:transition-colors",
						innerClassName,
					)}
					aria-describedby={[description ? descriptionId : false, hasError ? errorId : false]
						.filter(Boolean)
						.join(" ")}
				/>

				{!!isSuccess && (
					<span
						className={cn("pointer-events-none absolute right-2 flex justify-center text-success", {
							"top-3": !label,
							"top-[calc(1.75rem+(42px-16px)/2)]": label,
						})}
					>
						<FontAwesomeIcon icon={faCheck} fixedWidth />
					</span>
				)}

				{!!description && (
					<Typography
						id={descriptionId}
						className="mt-1 text-shades-dark text-sm"
						variant="paragraph"
					>
						{props.description}
					</Typography>
				)}

				{hasError && !!props.name && (
					<FormErrorMessage variant="danger" id={errorId}>
						<ErrorMessage name={props.name} />
					</FormErrorMessage>
				)}
			</div>
		);
	},
);
